import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Group as GroupIcon,
  EmojiEvents as AwardIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import { BUSINESS, PAGE_META } from '../constants/business';

const values = [
  {
    title: 'Trust & Integrity',
    description: 'We build lasting relationships through honest, transparent communication and ethical business practices.',
    icon: SecurityIcon,
  },
  {
    title: 'Professional Excellence', 
    description: 'Our experienced team delivers accurate, thorough title services with attention to every detail.',
    icon: AwardIcon,
  },
  {
    title: 'Client-Focused Service',
    description: 'Every client receives personalized attention and support throughout their real estate transaction.',
    icon: GroupIcon,
  },
  {
    title: 'Efficient Process',
    description: 'Streamlined workflows and technology enable fast turnaround times without compromising quality.',
    icon: SpeedIcon,
  },
];

const About: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>{PAGE_META["/about"].title}</title>
        <meta name="description" content={PAGE_META["/about"].description} />
        <meta property="og:title" content={PAGE_META["/about"].title} />
        <meta property="og:description" content={PAGE_META["/about"].description} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={BUSINESS.displayName} />
        <link rel="canonical" href={`${BUSINESS.siteUrl}/about`} />
      </Helmet>

      {/* Hero Section */}
      <Box
        sx={{
          backgroundColor: '#1e3a8a',
          color: 'white',
          py: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h1"
              component="h1"
              sx={{
                fontWeight: 700,
                mb: 2,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                color: 'white',
              }}
            >
              About MAD Title Agency
            </Typography>
            <Typography variant="h5" sx={{ maxWidth: 800, mx: 'auto', opacity: 0.9, color: '#94a3b8' }}>
              Your trusted partner for professional title services in New Jersey
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Company Story */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h2" component="h2" sx={{ mb: 3, color: '#64748b' }}>
              Our Story
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
              MAD Title Agency LLC was founded in 2015 by Gail C. Houck with a simple yet powerful mission: 
              to provide exceptional title services that protect our clients' most important investments. 
              Based in Woodbridge, NJ, we serve Middlesex County, Warren County, and the broader Eastern 
              New Jersey region with comprehensive title insurance, real estate closing services, and expert guidance.
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
              Gail is an accomplished business owner with a strong background in real estate. She formed 
              Alternate Legal Assistance in 1996 where she performed paralegal work for many real estate 
              attorneys. Alternate Legal became a well-known and respected name in the industry, which opened 
              the doors to expanding the business and offering title work.
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
              M.A.D. Title is named after Gail's grandson and has brought much luck to the business's great 
              success. What sets us apart is our unwavering commitment to client service, attention to detail, 
              and deep understanding of the local market. We don't just process transactions – we build 
              relationships and provide peace of mind through every step of your real estate journey.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: 4,
                backgroundColor: '#f8fafc',
                borderRadius: 2,
                border: '1px solid #e2e8f0',
              }}
            >
              <Typography variant="h4" component="h3" sx={{ mb: 3, color: '#64748b', textAlign: 'center' }}>
                By the Numbers
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#f59e0b' }}>
                      500+
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Successful Closings
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#f59e0b' }}>
                      48hr
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Average Turnaround
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#f59e0b' }}>
                      10+
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Years Experience
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#f59e0b' }}>
                      100%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Client Satisfaction
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Values Section */}
      <Box sx={{ backgroundColor: '#f8fafc', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h2" component="h2" sx={{ mb: 2, color: '#64748b' }}>
              Our Values
            </Typography>
            <Typography variant="h6" sx={{ maxWidth: 600, mx: 'auto', color: 'text.secondary' }}>
              The principles that guide everything we do
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Card
                    className="hover-lift"
                    sx={{
                      height: '100%',
                      textAlign: 'center',
                      p: 3,
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 64,
                        height: 64,
                        mx: 'auto',
                        mb: 2,
                        backgroundColor: '#1e3a8a',
                      }}
                    >
                      <Icon sx={{ fontSize: 32, color: 'white' }} />
                    </Avatar>
                    <Typography variant="h6" component="h3" sx={{ mb: 2, fontWeight: 600 }}>
                      {value.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {value.description}
                    </Typography>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>

      {/* Deep Dive: Our Expertise */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h2" component="h2" sx={{ mb: 2, color: '#64748b' }}>
            Comprehensive New Jersey Title Expertise
          </Typography>
          <Typography variant="h6" sx={{ maxWidth: 700, mx: 'auto', color: 'text.secondary', lineHeight: 1.6 }}>
            Deep local knowledge and specialized experience serving Eastern New Jersey's diverse real estate markets
          </Typography>
        </Box>

        <Grid container spacing={6}>
          <Grid item xs={12} md={6}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, color: '#1e3a8a', fontWeight: 600 }}>
              Founder's Background & Vision
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, color: '#374151' }}>
              Gail C. Houck founded MAD Title Agency LLC with over 25 years of experience in New Jersey real estate. 
              Her journey began in 1996 with Alternate Legal Assistance, where she provided paralegal services to 
              real estate attorneys throughout Middlesex County and beyond.
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, color: '#374151' }}>
              Through decades of working with attorneys, lenders, real estate agents, and homeowners, Gail identified 
              a need for more personalized, efficient title services. This insight led to the formation of MAD Title 
              Agency, combining her legal expertise with a client-first approach that prioritizes communication, 
              accuracy, and timely delivery.
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, color: '#374151' }}>
              <strong>The "MAD" Legacy:</strong> Named after Gail's grandson, MAD Title represents the continuation 
              of family values in business - treating every client like family, with the care and attention their 
              real estate investment deserves.
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.7, color: '#374151' }}>
              Today, Gail continues to personally oversee complex transactions while mentoring the next generation 
              of title professionals, ensuring MAD Title's commitment to excellence extends well into the future.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: 4,
                backgroundColor: '#f8fafc',
                borderRadius: 3,
                border: '1px solid #e2e8f0',
                height: 'fit-content',
              }}
            >
              <Typography variant="h5" component="h4" sx={{ mb: 3, color: '#1e3a8a', fontWeight: 600 }}>
                What Makes Us Different
              </Typography>
              <List>
                {[
                  {
                    title: "Personal Attention",
                    description: "Direct access to decision-makers, not just customer service representatives"
                  },
                  {
                    title: "Local Market Mastery", 
                    description: "Intimate knowledge of Middlesex County property patterns, common issues, and municipal requirements"
                  },
                  {
                    title: "Attorney Network",
                    description: "Established relationships with top real estate attorneys throughout New Jersey"
                  },
                  {
                    title: "Technology Integration",
                    description: "Modern systems combined with traditional attention to detail for optimal results"
                  },
                  {
                    title: "Flexible Scheduling",
                    description: "Accommodating closing times and locations to fit your busy schedule"
                  }
                ].map((differentiator, index) => (
                  <ListItem key={index} sx={{ py: 1, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                      <CheckIcon sx={{ fontSize: 18, color: '#f59e0b' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#1f2937', mb: 0.5 }}>
                          {differentiator.title}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" sx={{ color: '#6b7280', lineHeight: 1.5 }}>
                          {differentiator.description}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Local Market Knowledge */}
      <Box sx={{ backgroundColor: '#f8fafc', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h2" component="h2" sx={{ mb: 2, color: '#64748b' }}>
              Unmatched Local Market Knowledge
            </Typography>
            <Typography variant="h6" sx={{ maxWidth: 600, mx: 'auto', color: 'text.secondary', lineHeight: 1.6 }}>
              Decades of experience navigating New Jersey's unique real estate landscape
            </Typography>
          </Box>

          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: '100%',
                  p: 4,
                  border: '1px solid #e2e8f0',
                  borderRadius: 3,
                  '&:hover': {
                    boxShadow: '0 8px 24px rgba(30, 58, 138, 0.1)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                <Typography variant="h5" component="h3" sx={{ mb: 3, color: '#1e3a8a', fontWeight: 600 }}>
                  Historic Properties
                </Typography>
                <Typography variant="body1" paragraph sx={{ lineHeight: 1.7, color: '#374151' }}>
                  Extensive experience with properties dating back to colonial times. We understand the complexities 
                  of historical deeds, ancient easements, and boundary issues that can affect older properties 
                  throughout New Jersey.
                </Typography>
                <Typography variant="body1" sx={{ lineHeight: 1.7, color: '#374151' }}>
                  Our research capabilities extend to historical societies, courthouse archives, and genealogical 
                  records when necessary to resolve complex title issues.
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: '100%',
                  p: 4,
                  border: '1px solid #e2e8f0',
                  borderRadius: 3,
                  '&:hover': {
                    boxShadow: '0 8px 24px rgba(30, 58, 138, 0.1)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                <Typography variant="h5" component="h3" sx={{ mb: 3, color: '#1e3a8a', fontWeight: 600 }}>
                  Municipal Variations
                </Typography>
                <Typography variant="body1" paragraph sx={{ lineHeight: 1.7, color: '#374151' }}>
                  Each New Jersey municipality has unique requirements, from Woodbridge Township's specific recording 
                  procedures to Edison's development regulations. We maintain current knowledge of all local variations.
                </Typography>
                <Typography variant="body1" sx={{ lineHeight: 1.7, color: '#374151' }}>
                  Our team regularly attends municipal meetings and maintains relationships with local officials 
                  to stay ahead of regulatory changes.
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: '100%',
                  p: 4,
                  border: '1px solid #e2e8f0',
                  borderRadius: 3,
                  '&:hover': {
                    boxShadow: '0 8px 24px rgba(30, 58, 138, 0.1)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                <Typography variant="h5" component="h3" sx={{ mb: 3, color: '#1e3a8a', fontWeight: 600 }}>
                  Development Patterns
                </Typography>
                <Typography variant="body1" paragraph sx={{ lineHeight: 1.7, color: '#374151' }}>
                  Understanding regional development patterns helps us anticipate potential title issues. From 
                  post-war subdivisions to modern mixed-use developments, we know what to look for.
                </Typography>
                <Typography variant="body1" sx={{ lineHeight: 1.7, color: '#374151' }}>
                  This knowledge allows us to identify potential problems early and resolve them efficiently, 
                  saving time and preventing closing delays.
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Technology & Process */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h2" component="h2" sx={{ mb: 2, color: '#64748b' }}>
            Modern Technology, Traditional Values
          </Typography>
          <Typography variant="h6" sx={{ maxWidth: 600, mx: 'auto', color: 'text.secondary', lineHeight: 1.6 }}>
            Combining cutting-edge technology with time-tested attention to detail
          </Typography>
        </Box>

        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, color: '#1e3a8a', fontWeight: 600 }}>
              Advanced Research Capabilities
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, color: '#374151' }}>
              Our title search process combines traditional courthouse research with modern digital databases, 
              ensuring comprehensive coverage of all relevant records. We maintain subscriptions to multiple 
              professional databases and have direct access to county recording systems.
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, color: '#374151' }}>
              <strong>What this means for you:</strong> Faster turnaround times without sacrificing thoroughness. 
              We can often complete searches that might take others days in a matter of hours, while maintaining 
              the highest standards of accuracy.
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.7, color: '#374151' }}>
              Our quality control processes include multiple reviews by certified professionals, ensuring every 
              detail is caught before it reaches your closing table.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={3}
              sx={{
                p: 4,
                backgroundColor: '#fefdfb',
                border: '1px solid #f3f4f6',
                borderRadius: 3,
              }}
            >
              <Typography variant="h5" component="h4" sx={{ mb: 3, color: '#1e3a8a', fontWeight: 600 }}>
                Our Process Advantages
              </Typography>
              <List>
                {[
                  'Digital workflow management for real-time status updates',
                  'Automated deadline tracking and notification systems',
                  'Secure document sharing and electronic delivery',
                  'Integration with major underwriter systems',
                  'Mobile accessibility for field work and inspections',
                  'Comprehensive backup and disaster recovery systems'
                ].map((advantage, index) => (
                  <ListItem key={index} sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={advantage}
                      primaryTypographyProps={{ 
                        variant: 'body2',
                        sx: { fontSize: '1rem', color: '#374151' }
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Community Involvement */}
      <Box sx={{ backgroundColor: '#f8fafc', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h2" component="h2" sx={{ mb: 2, color: '#64748b' }}>
              Community Commitment
            </Typography>
            <Typography variant="h6" sx={{ maxWidth: 600, mx: 'auto', color: 'text.secondary', lineHeight: 1.6 }}>
              Active participation in the communities we serve throughout New Jersey
            </Typography>
          </Box>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Typography variant="h4" component="h3" sx={{ mb: 3, color: '#1e3a8a', fontWeight: 600 }}>
                Professional Network & Partnerships
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, color: '#374151' }}>
                We maintain active relationships with real estate professionals throughout Eastern New Jersey, 
                including top-producing agents, mortgage brokers, real estate attorneys, and home inspectors. 
                These partnerships ensure seamless transactions and the best possible service for our clients.
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, color: '#374151' }}>
                <strong>Educational Commitment:</strong> We regularly provide continuing education seminars for 
                real estate professionals on topics including title insurance updates, New Jersey law changes, 
                and best practices for smooth closings.
              </Typography>
              <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.7, color: '#374151' }}>
                Our team participates in local real estate associations, chamber of commerce events, and 
                professional networking groups to stay connected with industry trends and maintain our 
                reputation as trusted advisors.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h4" component="h3" sx={{ mb: 3, color: '#1e3a8a', fontWeight: 600 }}>
                Supporting First-Time Homebuyers
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, color: '#374151' }}>
                We understand that purchasing a home can be overwhelming, especially for first-time buyers. 
                Our team takes extra time to explain the title process, answer questions, and ensure buyers 
                understand exactly what protection their title insurance provides.
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, color: '#374151' }}>
                <strong>Free Resources:</strong> We provide comprehensive guides, checklists, and educational 
                materials to help buyers navigate the complex world of New Jersey real estate transactions 
                with confidence.
              </Typography>
              <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.7, color: '#374151' }}>
                Our commitment extends beyond closing day - we remain available to answer questions about 
                title issues, provide copies of documents, and offer guidance on property-related matters 
                long after the transaction is complete.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Certifications & Memberships */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h2" component="h2" sx={{ mb: 2, color: '#64748b' }}>
            Professional Certifications & Memberships
          </Typography>
          <Typography variant="h6" sx={{ color: 'text.secondary' }}>
            Maintaining the highest professional standards through continuous education and industry involvement
          </Typography>
        </Box>

        <Grid container spacing={4} justifyContent="center">
          <Grid item xs={12} sm={6} md={4}>
            <Card 
              sx={{ 
                textAlign: 'center', 
                p: 4, 
                height: '100%',
                border: '1px solid #e2e8f0',
                '&:hover': {
                  boxShadow: '0 8px 24px rgba(30, 58, 138, 0.1)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              <AwardIcon sx={{ fontSize: 48, color: '#f59e0b', mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Licensed Title Agent
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Fully licensed and regulated by the New Jersey Department of Banking and Insurance
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.9rem', color: '#6b7280' }}>
                NJ Title Insurance Producer License #1602165
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card 
              sx={{ 
                textAlign: 'center', 
                p: 4, 
                height: '100%',
                border: '1px solid #e2e8f0',
                '&:hover': {
                  boxShadow: '0 8px 24px rgba(30, 58, 138, 0.1)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              <SecurityIcon sx={{ fontSize: 48, color: '#f59e0b', mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                E&O Insurance Coverage
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Comprehensive errors and omissions insurance providing additional protection for your transaction
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.9rem', color: '#6b7280' }}>
                $1M+ coverage through A-rated insurance carrier
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card 
              sx={{ 
                textAlign: 'center', 
                p: 4, 
                height: '100%',
                border: '1px solid #e2e8f0',
                '&:hover': {
                  boxShadow: '0 8px 24px rgba(30, 58, 138, 0.1)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              <GroupIcon sx={{ fontSize: 48, color: '#f59e0b', mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Professional Memberships
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Active member of state and national title insurance associations
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.9rem', color: '#6b7280' }}>
                ALTA, NJLTIA, and local professional organizations
              </Typography>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default About; 